import gsap from 'gsap';
import gql from 'graphql-tag';
import className from 'classnames/bind';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useViewSize } from '@/providers/ViewSizeContext';
import { ImageGalleryFragment } from '@graphqlTypes';
import { chunkArray, getLinkData, scaleVal } from '@/core/utils';
import { Button, Container, Image, SectionHeader, RevealWrapper, HeadlineAnim } from '@components';
import ArrowRight from '@assets/svg/arrow-right.svg';
import { useInView } from 'motion/react';
import { AnimVals } from '@/utils/animUtils';
import { useClientMount } from '@/providers/ClientMountProvider';

import styles from './ImageGallery.module.scss';
import { usePageWrapperData } from '@/providers/PageWrapperContext';
const cx = className.bind(styles);

type ImageGalleryProps = Omit<ImageGalleryFragment, '__typename'> & {
  largeTitle?: boolean;
};

const TextHeader = ({ eyebrow, headline, subhead, cta, lineItems }: ImageGalleryFragment['textHeader']) => {
  const ctaData = getLinkData(cta);
  return (
    <div className={cx(["text-header"])}>
      <div className={cx(["content"])}>
        {eyebrow && <p className={cx(["eyebrow", "eyebrow-l"])}>{eyebrow}</p>}
        <h2 className={cx(["headline", "title-lc-m"])}>{headline}</h2>
        <p className={cx(["subhead", "text-m"])}>{subhead}</p>
        {lineItems && lineItems.length > 0 && (
          <ul className={cx(["line-items"])}>
            {lineItems.map((item, index) => (
              <li key={index} className={cx(["line-item"])}>
                <p className={cx(["line-item__headline", "text-l"])} role="heading" aria-level={3}>{item.headline}</p>
                <div className={cx(["text-m"])} dangerouslySetInnerHTML={{ __html: item.body }} />
              </li>
            ))}
          </ul>
        )}
        {ctaData && <Button className={cx(["cta"])} {...ctaData} /> }
      </div>
    </div>
  );
}

export default function ImageGallery(props: ImageGalleryProps) {
  const { mounted } = useClientMount();
  const { isDesktop, isReady } = useViewSize();
  const { imageGalleryImages: images, variant, standardHeader, textHeader, largeTitle } = props;
  const standard = variant[0] === 'standard';
  const showHeader = standard ? standardHeader && Boolean(standardHeader.headline || standardHeader.subhead) : textHeader && Boolean(textHeader.headline || textHeader.subhead);
  const galleryGridRefs = useRef<(HTMLDivElement | null)[]>([]);
  const { labels } = usePageWrapperData();
  const galleryWrapperEl = useRef<HTMLDivElement>(null);
  const galleryInnnerEl = useRef<HTMLDivElement>(null);
  const expandedRef = useRef(false);
  const [gridExpanded, setGridExpanded] = useState(expandedRef.current);
  const [showToggle, setShowToggle] = useState(false);
  const hasOverflow = useRef(false);
  const gridHeights = useRef<{ collapsed: number; expanded: number; }>({
    collapsed: 0,
    expanded: 0
  });

  const setGalleryGridRefs = (el: HTMLDivElement | null, index: number) => {
    galleryGridRefs.current[index] = el;
  };

  const handleToggleGrid = () => {
    if (!mounted || !isReady) return;

    gsap.to(galleryWrapperEl.current, {
      height: expandedRef.current ? gridHeights.current.collapsed : gridHeights.current.expanded,
      duration: 0.3,
      ease: 'power2.inOut',
    });
    expandedRef.current = !expandedRef.current;
    setGridExpanded(expandedRef.current);
  }


  const updateHeights = useCallback(() => {
    if (!mounted || !isReady) return;

    const collapsedHeight = galleryGridRefs.current[0]?.offsetHeight || 0;
    const expandedHeight = galleryInnnerEl.current?.scrollHeight + scaleVal(48) || 0;
    gridHeights.current = { collapsed: collapsedHeight, expanded: expandedHeight };
    if (hasOverflow.current) {
      gsap.set(galleryWrapperEl.current, { height: expandedRef.current ? expandedHeight : collapsedHeight });
    } else {
      gsap.set(galleryWrapperEl.current, { height: '' });
    }
  }, [mounted, isReady]);


  const chunkedImages = useMemo(() => {
    if (isDesktop) {
      return chunkArray(images, 8);
    } else {
      return chunkArray(images, 4);
    }
  }, [isDesktop]);

  useEffect(( ) => {
    if (!mounted || !isReady) return;

    if (isDesktop) {
      hasOverflow.current = images.length > 8;
    } else {
      hasOverflow.current = images.length > 4;
    }
    setShowToggle(hasOverflow.current);
    updateHeights();
  }, [mounted, isReady, isDesktop, updateHeights, images.length]);

  useEffect(() => {
    if (!mounted || !isReady) return;

    window.addEventListener('resize', updateHeights);
    updateHeights();
    return () => {
      window.removeEventListener('resize', updateHeights);
    }
  }, [mounted, isReady, updateHeights]);

  // -------------------- Animation

  const rootEl = useRef<HTMLElement | null>(null);

  const REVEAL = AnimVals;
  const triggerPercent = isDesktop ? REVEAL.lg.triggerPercent : REVEAL.sm.triggerPercent;
  const isInView = useInView(rootEl, { amount: triggerPercent, once: REVEAL.once });
  const isRevealEnabled = true;

  // -------------------- Render

  return (
    <section className={cx(["image-gallery"])} ref={rootEl}>
      <RevealWrapper isInView={isInView}>
        <Container>
          {showHeader && (standard ? (
            <SectionHeader
              largeTitle={largeTitle} {...standardHeader}
              isRevealEnabled={isRevealEnabled}
              isInView={isInView}
            />
          ) : (
            <TextHeader  {...textHeader} />
          ))}
          <div className={cx(["gallery-wrapper"])} ref={galleryWrapperEl}>
            <div className={cx(["gallery-inner"])} ref={galleryInnnerEl}>
              {chunkedImages.map((chunk, chunkIndex) => (
                <div className={cx(["gallery-grid"])} ref={(el) => setGalleryGridRefs(el, chunkIndex)} key={chunkIndex}>
                  {chunk.map((item, index) => (
                    <div  key={index} className={cx(["gallery-item"])}>
                      <Image image={item.image} className={cx(["gallery-image"])} />
                      {item.caption && <p className="text-m">{item.caption}</p>}
                    </div>
                  ))}
                </div>
              ))}
            </div>
            {showToggle && (
              <div className={cx(["gallery-overlay", { expanded: gridExpanded }])}>
                <button className={cx(["gallery-toggle", "inline-link"])} onClick={handleToggleGrid}>
                  <span>{gridExpanded ? labels?.viewLessLabel : labels?.viewMoreLabel}</span>
                  <ArrowRight className={cx(["icon"])} />
                </button>
              </div>
            )}
          </div>
        </Container>
      </RevealWrapper>
    </section>
  )
}

ImageGallery.fragments = {
  entry: gql`
    fragment ImageGallery on FlexiblePageContentContentImageGalleryLayout {
      variant
      standardHeader {
        headline
        subhead
      }
      textHeader {
        eyebrow
        headline
        subhead
        cta {
          linkType
          label
          externalLink
          page {
            node {
              uri
            }
          }
        }
        lineItems {
          headline
          body
        }
      }
      imageGalleryImages: images {
        caption
        image {
          ...ACFMedia
        }
      }
    }
  `,
};
